// Inline prompt cards for agent:approval + agent:ask_user.
//
// Both render at the bottom of the message stream (between the last
// bubble and the composer). They block the run until the user
// responds, so the composer should be disabled while either is
// pending — that wiring lives in the chat view, not here.

import { useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { PendingApproval, PendingQuestion } from "../../lib/use-chat";

interface ApprovalCardProps {
  pending: PendingApproval;
  onRespond: (approved: boolean, reason?: string) => void;
}

export function ApprovalCard({ pending, onRespond }: ApprovalCardProps) {
  const [showFull, setShowFull] = useState(false);
  const argsText = formatToolArgs(pending.toolArgs);
  const argsPreview = argsText.length > 200 ? argsText.slice(0, 200) + "…" : argsText;
  const dangerous = !!pending.dangerous;

  return (
    <div className="agent-prompt" data-tone={dangerous ? "danger" : "default"}>
      <div className="agent-prompt-head">
        <span className="agent-prompt-icon">
          <Icon name={dangerous ? "warn" : "shield"} size={14} />
        </span>
        <div className="agent-prompt-title">
          <div className="agent-prompt-eyebrow">
            {dangerous ? "Approval needed · careful" : "Approval needed"}
          </div>
          <div className="agent-prompt-name">
            <code>{pending.toolName}</code>
          </div>
        </div>
      </div>

      {dangerous && pending.dangerousReason && (
        <p className="agent-prompt-reason">{pending.dangerousReason}</p>
      )}

      {argsText && (
        <pre
          className="agent-prompt-args"
          onClick={() => setShowFull((v) => !v)}
          title={showFull ? "Click to collapse" : "Click to expand"}
        >
          {showFull ? argsText : argsPreview}
        </pre>
      )}

      <div className="agent-prompt-actions">
        <button className="btn" onClick={() => onRespond(false, "user denied")} type="button">
          Deny
        </button>
        <button className="btn btn-primary" onClick={() => onRespond(true)} type="button">
          Allow
        </button>
      </div>
    </div>
  );
}

interface AskCardProps {
  pending: PendingQuestion;
  onRespond: (answer: string) => void;
}

export function AskCard({ pending, onRespond }: AskCardProps) {
  const [draft, setDraft] = useState("");
  const hasOptions = !!(pending.options && pending.options.length > 0);

  return (
    <div className="agent-prompt" data-tone="ask">
      <div className="agent-prompt-head">
        <span className="agent-prompt-icon">
          <Icon name="info" size={14} />
        </span>
        <div className="agent-prompt-title">
          <div className="agent-prompt-eyebrow">Agent question</div>
          <div className="agent-prompt-question">{pending.question}</div>
        </div>
      </div>

      {hasOptions ? (
        <div className="agent-prompt-options">
          {pending.options!.map((opt, i) => (
            <button
              key={i}
              className="agent-prompt-option"
              onClick={() => onRespond(opt.label)}
              type="button"
            >
              <span className="agent-prompt-option-label">{opt.label}</span>
              {opt.description && (
                <span className="agent-prompt-option-desc">{opt.description}</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="agent-prompt-freeform">
          <textarea
            rows={2}
            value={draft}
            onInput={(e) => setDraft((e.currentTarget as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                if (draft.trim()) onRespond(draft.trim());
              }
            }}
            placeholder="Type your answer… (⌘/Ctrl+Enter to send)"
          />
          <button
            className="btn btn-primary"
            disabled={!draft.trim()}
            onClick={() => onRespond(draft.trim())}
            type="button"
          >
            <Icon name="send" size={13} />
            <span>Reply</span>
          </button>
        </div>
      )}
    </div>
  );
}

function formatToolArgs(args: unknown): string {
  if (args == null) return "";
  if (typeof args === "string") return args;
  try {
    return JSON.stringify(args, null, 2);
  } catch {
    return String(args);
  }
}
